import type { AudioProcessorOptions, TrackProcessor } from 'livekit-client'
import { Track } from 'livekit-client'

import {
  livekitUrlToNoiseFilterUrl,
  logNoiseFilterEvent,
} from '@/src/lib/noiseFilter'
import {
  decodePcmFrame,
  encodePcmFrame,
  floatToI16,
  i16ToFloat,
} from '@/src/lib/noiseFilterProtocol'
import {
  NOISE_FILTER_WORKLET_NAME,
  NOISE_FILTER_WORKLET_SOURCE,
} from '@/src/lib/noiseFilterWorklet'

/**
 * Сколько миллисекунд микрофона копить в один кадр на сокет.
 *
 * 20 мс — привычный размер для речи: меньше кадров в секунду, чем квант
 * worklet, и DeepFilterNet успевает обработать без лишней нарезки.
 */
const FRAME_MS = 20

/** Если рукопожатие сокета не завершилось, откатываемся на сырой микрофон. */
const WS_OPEN_TIMEOUT_MS = 5_000

/** Сколько раз открываем `/nf`: Caddy за L4 иногда сбрасывает первый Upgrade. */
const WS_OPEN_ATTEMPTS = 4

/** Пауза между попытками, растёт с номером попытки. */
const WS_OPEN_RETRY_MS = 400

const workletByContext = new WeakMap<AudioContext, Promise<void>>()

export interface ServerNoiseFilterProcessorOptions {
  livekitUrl: string
  accessToken: string
  /**
   * Сокет умер после того, как процессор уже висел на треке.
   * Хук снимает процессор и публикует сырой микрофон.
   */
  onFatal?: () => void
}

/**
 * LiveKit-процессор: микрофон → WebSocket на ноду → чистый MediaStreamTrack.
 *
 * Браузерная эхокомпенсация остаётся на исходном захвате. Сюда приходит
 * уже «сухой» относительно колонок сигнал. Если сокет не открылся,
 * `init` бросает — вызывающий код не оставляет человека без микрофона.
 */
export class ServerNoiseFilterProcessor implements TrackProcessor<
  Track.Kind.Audio,
  AudioProcessorOptions
> {
  readonly name = 'server-noise-filter'

  processedTrack?: MediaStreamTrack

  private readonly livekitUrl: string

  private readonly accessToken: string

  private readonly onFatal?: () => void

  private audioContext?: AudioContext

  private sourceNode?: MediaStreamAudioSourceNode

  private workletNode?: AudioWorkletNode

  private destinationNode?: MediaStreamAudioDestinationNode

  private websocket?: WebSocket

  private seq = 0

  private captureChunks: Float32Array[] = []

  private captureLength = 0

  private frameSize = 0

  private sentAt = new Map<number, number>()

  private rttSamples = 0

  private rttTotalMs = 0

  private destroyed = false

  private fatalNotified = false

  private handleVisibilityChange?: () => void

  constructor(options: ServerNoiseFilterProcessorOptions) {
    this.livekitUrl = options.livekitUrl
    this.accessToken = options.accessToken
    this.onFatal = options.onFatal
  }

  /**
   * Собирает граф Web Audio и открывает сокет к `/nf`.
   */
  async init(opts: AudioProcessorOptions): Promise<void> {
    this.destroyed = false
    this.fatalNotified = false

    this.handleVisibilityChange = () => {
      if (this.destroyed) {
        return
      }

      if (document.visibilityState === 'visible') {
        void this.resumeAfterHidden()
      }
    }
    document.addEventListener('visibilitychange', this.handleVisibilityChange)

    try {
      await this.startGraph(opts)
      await this.openSocket()
      logNoiseFilterEvent('processor_ready', {
        host: livekitUrlToNoiseFilterUrl(this.livekitUrl),
      })
    } catch (error) {
      await this.destroy()
      throw error
    }
  }

  /**
   * Подключает новый микрофон после смены устройства и сбрасывает состояние модели.
   *
   * Новый сокет нужен потому, что GRU на сервере помнит предыдущий вход.
   * Трек на выходе тот же, LiveKit не обязан заново делать replaceTrack.
   */
  async restart(opts: AudioProcessorOptions): Promise<void> {
    this.detachSource()
    this.resetCapture()
    await this.closeSocket()
    await this.startGraph(opts)
    await this.openSocket()
    logNoiseFilterEvent('processor_restarted')
  }

  /**
   * Рвёт сокет и граф. Исходный AudioContext комнаты не закрываем.
   */
  async destroy(): Promise<void> {
    this.destroyed = true

    if (this.handleVisibilityChange) {
      document.removeEventListener('visibilitychange', this.handleVisibilityChange)
      this.handleVisibilityChange = undefined
    }

    this.detachSource()
    await this.closeSocket()

    this.workletNode?.disconnect()
    this.workletNode = undefined
    this.destinationNode?.disconnect()
    this.destinationNode = undefined
    this.processedTrack = undefined
    this.audioContext = undefined
  }

  /**
   * Собирает source → worklet → destination. Worklet и выходной трек переиспользуем.
   */
  private async startGraph(opts: AudioProcessorOptions): Promise<void> {
    // При restart() LiveKit не кладёт audioContext в opts (в отличие от init),
    // поэтому подхватываем тот, что сохранили при инициализации.
    const audioContext = opts.audioContext ?? this.audioContext

    if (!audioContext) {
      throw new Error('AudioContext is required for server noise filter')
    }

    this.audioContext = audioContext
    this.frameSize = Math.max(1, Math.round((audioContext.sampleRate * FRAME_MS) / 1000))

    await ensureNoiseFilterWorklet(audioContext)

    if (!this.workletNode) {
      this.workletNode = new AudioWorkletNode(audioContext, NOISE_FILTER_WORKLET_NAME, {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        outputChannelCount: [1],
      })
      this.workletNode.port.onmessage = (event: MessageEvent) => {
        this.onWorkletCapture(event)
      }
    }

    if (!this.destinationNode) {
      this.destinationNode = audioContext.createMediaStreamDestination()
      this.workletNode.connect(this.destinationNode)
      this.processedTrack = this.destinationNode.stream.getAudioTracks()[0]
    }

    this.sourceNode = audioContext.createMediaStreamSource(new MediaStream([opts.track]))
    this.sourceNode.connect(this.workletNode)

    if (audioContext.state === 'suspended') {
      await audioContext.resume().catch(() => undefined)
    }
  }

  /**
   * Копит кванты worklet и, когда сокет жив, отправляет кадры по 20 мс.
   */
  private onWorkletCapture(event: MessageEvent): void {
    if (this.destroyed || event.data?.type !== 'capture') {
      return
    }

    const samples = new Float32Array(event.data.samples)

    this.captureChunks.push(samples)
    this.captureLength += samples.length

    const maxCapture = this.frameSize * 10

    while (this.captureLength > maxCapture && this.captureChunks.length > 0) {
      const dropped = this.captureChunks.shift()
      this.captureLength -= dropped?.length ?? 0
    }

    while (
      this.captureLength >= this.frameSize &&
      this.websocket?.readyState === WebSocket.OPEN
    ) {
      this.sendNextFrame()
    }
  }

  /**
   * Снимает один кадр из накопителя и отправляет его на сервис.
   */
  private sendNextFrame(): void {
    const ws = this.websocket

    if (!ws || ws.readyState !== WebSocket.OPEN) {
      return
    }

    const frame = new Float32Array(this.frameSize)
    let filled = 0

    while (filled < this.frameSize && this.captureChunks.length > 0) {
      const chunk = this.captureChunks[0]

      if (!chunk) {
        break
      }

      const need = this.frameSize - filled
      const take = Math.min(need, chunk.length)

      frame.set(chunk.subarray(0, take), filled)
      filled += take

      if (take === chunk.length) {
        this.captureChunks.shift()
      } else {
        this.captureChunks[0] = chunk.subarray(take)
      }
    }

    this.captureLength -= frame.length

    const seq = this.seq
    this.seq = (this.seq + 1) >>> 0
    this.sentAt.set(seq, performance.now())

    if (this.sentAt.size > 200) {
      const oldest = this.sentAt.keys().next().value

      if (typeof oldest === 'number') {
        this.sentAt.delete(oldest)
      }
    }

    const sampleRate = this.audioContext?.sampleRate ?? 48000
    ws.send(encodePcmFrame(seq, sampleRate, floatToI16(frame)))
  }

  /**
   * Собирает wss://нода/nf с JWT в query, как LiveKit делает для `/rtc`.
   */
  private socketUrl(): string {
    const base = livekitUrlToNoiseFilterUrl(this.livekitUrl)
    const url = new URL(base)

    url.searchParams.set('access_token', this.accessToken)

    return url.toString()
  }

  /**
   * Открывает сокет и подписывается на кадры. Несколько попыток: первый
   * Upgrade через Caddy L4 иногда не доходит до сервиса, повтор уже проходит.
   */
  private async openSocket(): Promise<void> {
    let lastError: Error | undefined

    for (let attempt = 1; attempt <= WS_OPEN_ATTEMPTS; attempt += 1) {
      if (this.destroyed) {
        return
      }

      try {
        const websocket = await this.connectSocket()

        if (this.destroyed) {
          websocket.close()
          return
        }

        websocket.onmessage = (event: MessageEvent) => {
          this.onSocketMessage(event)
        }

        websocket.onclose = () => {
          if (!this.destroyed) {
            this.notifyFatal('socket_closed')
          }
        }

        this.websocket = websocket

        while (
          this.captureLength >= this.frameSize &&
          this.websocket.readyState === WebSocket.OPEN
        ) {
          this.sendNextFrame()
        }

        return
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error))
        logNoiseFilterEvent('processor_ws_retry', {
          attempt,
          attempts: WS_OPEN_ATTEMPTS,
          message: lastError.message,
        })

        if (attempt < WS_OPEN_ATTEMPTS) {
          await sleep(WS_OPEN_RETRY_MS * attempt)
        }
      }
    }

    throw lastError ?? new Error('noise filter websocket error')
  }

  /**
   * Одно рукопожатие WebSocket. Таймаут — отдельная ошибка, не путать с onerror.
   */
  private connectSocket(): Promise<WebSocket> {
    const url = this.socketUrl()

    return new Promise<WebSocket>((resolve, reject) => {
      const ws = new WebSocket(url)
      ws.binaryType = 'arraybuffer'

      const timer = window.setTimeout(() => {
        ws.onopen = null
        ws.onerror = null
        ws.close()
        reject(new Error('noise filter websocket timeout'))
      }, WS_OPEN_TIMEOUT_MS)

      ws.onopen = () => {
        window.clearTimeout(timer)
        resolve(ws)
      }

      ws.onerror = () => {
        window.clearTimeout(timer)
        ws.onopen = null
        ws.onerror = null
        ws.close()
        reject(new Error('noise filter websocket error'))
      }
    })
  }

  /**
   * Принимает чистый PCM, считает круг и отдаёт сэмплы в worklet.
   */
  private onSocketMessage(event: MessageEvent): void {
    if (this.destroyed || !(event.data instanceof ArrayBuffer)) {
      return
    }

    try {
      const frame = decodePcmFrame(event.data)
      const sent = this.sentAt.get(frame.seq)

      if (sent !== undefined) {
        this.sentAt.delete(frame.seq)
        this.rttTotalMs += performance.now() - sent
        this.rttSamples += 1

        if (this.rttSamples === 1 || this.rttSamples % 25 === 0) {
          logNoiseFilterEvent('rtt', {
            ms: Math.round(this.rttTotalMs / this.rttSamples),
            samples: this.rttSamples,
          })
        }
      }

      const playback = i16ToFloat(frame.samples)
      this.workletNode?.port.postMessage({ type: 'playback', samples: playback.buffer }, [
        playback.buffer,
      ])
    } catch (error) {
      logNoiseFilterEvent('bad_frame', {
        message: error instanceof Error ? error.message : String(error),
      })
    }
  }

  /**
   * Закрывает сокет без повторного onFatal: destroy и смена устройства сами рвут связь.
   */
  private async closeSocket(): Promise<void> {
    const ws = this.websocket
    this.websocket = undefined

    if (!ws) {
      return
    }

    ws.onclose = null
    ws.onmessage = null
    ws.onerror = null

    if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
      ws.close()
    }
  }

  /**
   * Отключает только вход с микрофона, выходной трек для LiveKit оставляем.
   */
  private detachSource(): void {
    this.sourceNode?.disconnect()
    this.sourceNode = undefined
  }

  /**
   * Забывает неотправенные кадры, чтобы после restart не уехал чужой хвост.
   */
  private resetCapture(): void {
    this.captureChunks = []
    this.captureLength = 0
    this.sentAt.clear()
  }

  /**
   * Сообщает хуку, что круг умер и нужно публиковать сырой микрофон.
   */
  private notifyFatal(reason: string): void {
    if (this.fatalNotified || this.destroyed) {
      return
    }

    this.fatalNotified = true
    logNoiseFilterEvent('processor_fatal', { reason })
    this.onFatal?.()
  }

  /**
   * Возвращаемся во вкладку: браузер замораживает AudioContext в фоне, поэтому
   * ворклет молчит и в комнату идёт тишина. Резюмируем контекст и при необходимости
   * поднимаем свежий сокет, если старый не пережил уход в тень.
   */
  private async resumeAfterHidden(): Promise<void> {
    if (!this.audioContext) {
      return
    }

    if (this.audioContext.state === 'closed') {
      this.notifyFatal('audio_context_closed')
      return
    }

    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume().catch(() => undefined)
    }

    if (this.destroyed) {
      return
    }

    if (this.websocket?.readyState !== WebSocket.OPEN) {
      try {
        await this.closeSocket()
        await this.openSocket()
      } catch {
        this.notifyFatal('socket_reconnect_after_hidden')
      }
    }
  }
}

/**
 * Грузит worklet один раз на AudioContext комнаты.
 *
 * Blob URL отзываем после addModule: сам модуль уже в контексте,
 * повторный init при смене устройства не качает скрипт заново.
 */
async function ensureNoiseFilterWorklet(audioContext: AudioContext): Promise<void> {
  const pending = workletByContext.get(audioContext)

  if (pending) {
    await pending
    return
  }

  const blob = new Blob([NOISE_FILTER_WORKLET_SOURCE], { type: 'application/javascript' })
  const url = URL.createObjectURL(blob)
  const loading = audioContext.audioWorklet.addModule(url).finally(() => {
    URL.revokeObjectURL(url)
  })

  workletByContext.set(audioContext, loading)
  await loading
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms)
  })
}
